/**
 * Fit Creamery custom — fc-menu-drawer.js
 *
 * Open/close behaviour for our slide-out menu drawer.
 *
 * Loaded once, from snippets/fc-nav-trigger.liquid via asset_url (deferred),
 * NOT from layout/theme.liquid and NOT from the drawer section — that keeps the
 * feature to zero extra <head> hooks while guaranteeing a single copy. See the
 * guard below and CUSTOMIZATIONS.md.
 *
 * DESIGN NOTES
 *   - Trigger clicks are delegated off `document` rather than bound to the
 *     buttons directly. There are two triggers (desktop and mobile, rendered
 *     into separate Eurus containers), and the header can be re-rendered by
 *     the theme's sticky logic, so delegation is both shorter and safer than
 *     re-binding.
 *   - No Alpine dependency for open/close: the drawer is our markup and Eurus
 *     boots Alpine late, so waiting on it would only delay the first tap. We
 *     use FC.onCartToggle solely to close ourselves when the cart drawer opens.
 *   - Scroll lock is a class on <html> (`fc-menu-open`), mirroring the existing
 *     `fc-cart-open` convention so fc-hide-floating-on-cart.js keeps working.
 *
 * Standalone; safe to keep across Eurus theme updates.
 */
(function () {
  'use strict';

  // Single-init guard. Two copies of this file each register their own document
  // click listener while sharing one DOM class, so after the first close one
  // instance opens the drawer and the other immediately closes it — the button
  // goes dead. That happened on live when the <script> was left in both the
  // section and the trigger snippet. The tag lives in one place now; this makes
  // a repeat impossible rather than merely unlikely.
  if (window.__fcMenuDrawer) return;
  window.__fcMenuDrawer = true;

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]),' +
    'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var drawer = null;
  var panel = null;
  var lastTrigger = null;

  function find() {
    if (!drawer || !document.contains(drawer)) {
      drawer = document.querySelector('[data-fc-menu]');
      panel = drawer ? drawer.querySelector('[data-fc-menu-panel]') : null;
    }
    return !!drawer;
  }

  function triggers() {
    return document.querySelectorAll('[data-fc-menu-toggle]');
  }

  function isOpen() {
    return !!drawer && drawer.classList.contains('is-open');
  }

  function open(fromEl) {
    if (!find() || isOpen()) return;

    lastTrigger = fromEl || null;
    drawer.classList.add('is-open');
    drawer.removeAttribute('aria-hidden');
    document.documentElement.classList.add('fc-menu-open');

    Array.prototype.forEach.call(triggers(), function (t) {
      t.setAttribute('aria-expanded', 'true');
    });

    // Move focus into the panel so keyboard and screen-reader users land in the
    // menu rather than continuing through the header behind the overlay.
    var first = panel && panel.querySelector(FOCUSABLE);
    if (first) first.focus();
  }

  function close() {
    if (!find() || !isOpen()) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('fc-menu-open');

    Array.prototype.forEach.call(triggers(), function (t) {
      t.setAttribute('aria-expanded', 'false');
    });

    // Return focus to whichever trigger opened it, if it is still on the page.
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
    lastTrigger = null;
  }

  /**
   * Keep Tab cycling inside the panel while it is open.
   *
   * The drawer covers the page, so letting focus walk out into the header
   * behind it would strand keyboard users on invisible controls.
   */
  function trapTab(e) {
    if (!panel) return;

    var items = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-fc-menu-toggle]');
    if (toggle) {
      e.preventDefault();
      if (isOpen()) close();
      else open(toggle);
      return;
    }

    if (e.target.closest('[data-fc-menu-close]')) {
      e.preventDefault();
      close();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (!isOpen()) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'Tab') {
      trapTab(e);
    }
  });

  // Never leave both drawers open at once — the cart slides in from the right,
  // ours from the left, and two overlays stack their dimming.
  if (window.FC && typeof window.FC.onCartToggle === 'function') {
    window.FC.onCartToggle(function (cartOpen) {
      if (cartOpen) close();
    });
  }

  // A bfcache restore replays the DOM exactly as it was left, so a drawer that
  // was open at navigation time comes back open with the scroll lock still on.
  // Same class of bug as the cart's "empty on Back" issue noted in
  // product-bundler.js.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) close();
  });
})();
